// Mandato Pratica Form Component - STABILIZED VERSION
// Fixed: React runtime crash (insertBefore/NotFoundError)

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Save, Printer, Loader2, CheckCircle2, User, FileText,
  Building2, Briefcase, ClipboardCheck, Shield, UserCheck, PlusCircle,
  AlertCircle, Search, ChevronDown
} from "lucide-react";
import { cn } from "@/lib/utils";
import api from "../api";

// 30/06/2026 — TIPO_PRATICA_OPTIONS hardcoded rimossa.
// Il campo, ora rinominato "Ente Coinvolto", legge le opzioni dalla
// tabella configurabile `ente_option` (vedi Impostazioni → Tabelle di
// configurazione). Il valore salvato resta nel campo `practiceType`
// per backward compat completa con i mandati esistenti.

// Italian Codice Fiscale validation
function validateCodiceFiscale(cf) {
  if (!cf || cf.trim() === "") return { valid: true, error: null };
  
  const cleaned = cf.toUpperCase().replace(/\s/g, '');
  
  if (cleaned.length !== 16 && cleaned.length !== 11) {
    return { valid: false, error: "Il Codice Fiscale deve essere di 16 caratteri (persona fisica) o 11 caratteri (azienda)" };
  }
  
  if (cleaned.length === 16) {
    const cfRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
    if (!cfRegex.test(cleaned)) {
      return { valid: false, error: "Formato Codice Fiscale non valido" };
    }
  }
  
  if (cleaned.length === 11) {
    if (!/^\d{11}$/.test(cleaned)) {
      return { valid: false, error: "Il codice a 11 cifre deve contenere solo numeri" };
    }
  }
  
  return { valid: true, error: null };
}

// Infer branding from practice type
function inferBrandingFromPracticeType(practiceType) {
  if (!practiceType) return "studio";
  const lower = practiceType.toLowerCase();
  if (lower.includes("agenzia") || lower.includes("gestionale agenzia")) {
    return "agenzia";
  }
  return "studio";
}

// Field Error Component - STABLE
function FieldError({ error }) {
  if (!error) return null;
  return (
    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
      <AlertCircle className="h-3 w-3" />
      {error}
    </p>
  );
}

// STABLE Client Search Component - No Command/Popover (avoids DOM mutation issues)
function ClientSearchSelect({ 
  value, 
  clients, 
  onSelect, 
  onCreateNew,
  error 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = React.useRef(null);
  
  // Filter clients
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!searchQuery.trim()) return clients.slice(0, 20); // Limit initial display
    const query = searchQuery.toLowerCase();
    return clients.filter(c => 
      c.fullName?.toLowerCase().includes(query) ||
      c.taxCode?.toLowerCase().includes(query)
    ).slice(0, 20);
  }, [clients, searchQuery]);
  
  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);
  
  const handleSelect = useCallback((client) => {
    onSelect(client);
    setIsOpen(false);
    setSearchQuery("");
  }, [onSelect]);
  
  const handleCreateNew = useCallback(() => {
    if (onCreateNew && searchQuery.trim()) {
      onCreateNew(searchQuery.trim());
    }
    setIsOpen(false);
    setSearchQuery("");
  }, [onCreateNew, searchQuery]);
  
  const showCreateOption = searchQuery.trim().length > 2 && filteredClients.length === 0;
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md bg-white",
          "hover:bg-slate-50 transition-colors text-left",
          error ? "border-red-500" : "border-slate-200",
          !value && "text-slate-500"
        )}
        data-testid="mandato-client-search"
      >
        <span className="truncate">{value || "Cerca o seleziona cliente..."}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 ml-2 transition-transform", isOpen && "rotate-180")} />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca cliente..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                autoFocus
                /* Vanilla input → suppress browser autofill manually
                   (Edge personal suggestions overlay).
                   See /app/frontend/src/lib/no-autofill.js */
                autoComplete="off-no-suggest"
                autoCorrect="off"
                spellCheck={false}
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
              />
            </div>
          </div>
          
          {/* Results List */}
          <div className="overflow-y-auto max-h-48">
            {/* Create new option */}
            {showCreateOption && (
              <button
                type="button"
                onClick={handleCreateNew}
                className="w-full px-3 py-2 text-left text-sm text-teal-600 font-medium hover:bg-teal-50 flex items-center gap-2 border-b"
              >
                <PlusCircle className="h-4 w-4" />
                + Crea cliente "{searchQuery}"
              </button>
            )}
            
            {/* Client list */}
            {filteredClients.length > 0 ? (
              filteredClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleSelect(client)}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex flex-col",
                    value === client.fullName && "bg-teal-50"
                  )}
                >
                  <span className="font-medium">{client.fullName}</span>
                  {client.taxCode && (
                    <span className="text-xs text-slate-500">{client.taxCode}</span>
                  )}
                </button>
              ))
            ) : (
              !showCreateOption && (
                <div className="px-3 py-4 text-sm text-slate-500 text-center">
                  {searchQuery ? "Nessun cliente trovato" : "Digita per cercare..."}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Form Component
export default function MandatoPraticaForm({ 
  editingForm, 
  clients, 
  operators, 
  cases, 
  currentOperator, 
  onBack, 
  onSaved,
  onOpenClientDialog,
  // 30/06/2026 — Config tables (Tipo Pratica + Ente Coinvolto).
  // Il campo "Ente Coinvolto" del form Mandato (era etichettato "Tipo
  // Pratica" con lista hardcoded 6 enti — allineamento semantico
  // completato) legge da `configTables.ente_options` (tutti gli scope).
  configTables = { practice_types: [], ente_options: [] },
}) {
  // Form state - all fields in single state object
  const [formData, setFormData] = useState({
    clientName: "",
    clientId: "",
    taxCode: "",
    vatNumber: "",
    clientAddress: "",
    clientEmail: "",
    clientPhone: "",
    clientSDI: "",
    repName: "",
    repTaxCode: "",
    repPhone: "",
    repEmail: "",
    practiceType: "",
    practiceDescription: "",
    intakeDate: new Date().toISOString().split("T")[0],
    operator: currentOperator?.fullName || "",
    operatorId: currentOperator?.id || "",
    notes: "",
    linkedCaseId: ""
  });
  
  const [brandingContext, setBrandingContext] = useState("studio");
  const [brandingLocked, setBrandingLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showRepresentative, setShowRepresentative] = useState(false);

  // Load editing form data - STABLE effect
  useEffect(() => {
    if (editingForm) {
      const data = editingForm.formData || {};
      setFormData({
        clientName: data.clientName || editingForm.clientName || "",
        clientId: editingForm.clientId || "",
        taxCode: data.taxCode || "",
        vatNumber: data.vatNumber || "",
        clientAddress: data.clientAddress || "",
        clientEmail: data.clientEmail || "",
        clientPhone: data.clientPhone || "",
        clientSDI: data.clientSDI || "",
        repName: data.repName || "",
        repTaxCode: data.repTaxCode || "",
        repPhone: data.repPhone || "",
        repEmail: data.repEmail || "",
        practiceType: data.practiceType || "",
        practiceDescription: data.practiceDescription || "",
        intakeDate: data.intakeDate || new Date().toISOString().split("T")[0],
        operator: data.operator || editingForm.operatorName || "",
        operatorId: editingForm.operatorId || "",
        notes: data.notes || "",
        linkedCaseId: editingForm.caseId || ""
      });
      setBrandingContext(editingForm.brandingContext || "studio");
      if (editingForm.caseId) {
        setBrandingLocked(true);
      }
      if (data.repName || data.repTaxCode || data.repPhone || data.repEmail) {
        setShowRepresentative(true);
      }
    }
  }, [editingForm]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.clientName.trim()) newErrors.clientName = "Campo obbligatorio";
    if (!formData.taxCode.trim()) newErrors.taxCode = "Campo obbligatorio";
    if (!formData.practiceType) newErrors.practiceType = "Campo obbligatorio";
    if (!formData.practiceDescription.trim()) newErrors.practiceDescription = "Campo obbligatorio";
    if (!formData.intakeDate) newErrors.intakeDate = "Campo obbligatorio";
    if (!formData.operator.trim()) newErrors.operator = "Campo obbligatorio";
    
    if (formData.repTaxCode && formData.repTaxCode.trim()) {
      const cfValidation = validateCodiceFiscale(formData.repTaxCode);
      if (!cfValidation.valid) {
        newErrors.repTaxCode = cfValidation.error;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // ─── Ente Coinvolto options (30/06/2026) ─────────────────────────────
  // Attinge dalla tabella configurabile `ente_option` (tutti gli scope).
  // Include automaticamente il valore corrente (`formData.practiceType`)
  // anche se disattivato o legacy (es. "CCIAA", "CCIAA-SUAP-AE" pre-migrazione)
  // per garantire piena backward compatibility con i mandati già salvati.
  const enteCoinvoltoOptions = useMemo(() => {
    const activeNames = (configTables.ente_options || [])
      .filter(o => o.isActive)
      .map(o => o.name);
    const current = (formData.practiceType || "").trim();
    const merged = new Set(activeNames);
    if (current) merged.add(current);
    return Array.from(merged).sort((a, b) => a.localeCompare(b, "it"));
  }, [configTables.ente_options, formData.practiceType]);

  // Update field - STABLE
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (prev[field]) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
    setSaveSuccess(false);
  }, []);

  // Handle client selection - auto-fill ALL data
  const handleClientSelect = useCallback((client) => {
    if (!client) return;
    
    let fullAddress = client.address || "";
    if (client.postalCode || client.city || client.province) {
      const cityParts = [client.postalCode, client.city];
      if (client.province) cityParts.push(`(${client.province})`);
      if (fullAddress) fullAddress += ", ";
      fullAddress += cityParts.filter(Boolean).join(" ");
    }
    
    setFormData(prev => ({
      ...prev,
      clientName: client.fullName || "",
      clientId: client.id || "",
      taxCode: client.taxCode || "",
      vatNumber: client.vatNumber || "",
      clientAddress: fullAddress,
      clientEmail: client.email || "",
      clientPhone: client.phone || "",
      clientSDI: client.sdiCode || client.pec || ""
    }));
    setErrors(prev => {
      const { clientName: _, taxCode: __, ...rest } = prev;
      return rest;
    });
    setSaveSuccess(false);
  }, []);

  // Handle create new client
  const handleCreateNewClient = useCallback((initialName) => {
    if (onOpenClientDialog) {
      onOpenClientDialog(initialName);
    }
  }, [onOpenClientDialog]);

  // Handle case selection
  const handleCaseSelect = useCallback((caseId) => {
    if (!caseId || caseId === "none") {
      setFormData(prev => ({ ...prev, linkedCaseId: "" }));
      setBrandingLocked(false);
      return;
    }
    
    const selectedCase = cases?.find(c => c.id === caseId);
    if (selectedCase) {
      const inferredBranding = inferBrandingFromPracticeType(selectedCase.practiceType);
      setFormData(prev => ({
        ...prev,
        linkedCaseId: caseId,
        practiceDescription: selectedCase.subject || prev.practiceDescription
      }));
      
      const linkedClient = clients?.find(c => c.id === selectedCase.clientId);
      if (linkedClient) {
        handleClientSelect(linkedClient);
      }
      
      setBrandingContext(inferredBranding);
      setBrandingLocked(true);
    }
    setSaveSuccess(false);
  }, [cases, clients, handleClientSelect]);

  // Save form
  const handleSave = useCallback(async (andPrint = false) => {
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    
    try {
      const linkedCase = formData.linkedCaseId ? cases?.find(c => c.id === formData.linkedCaseId) : null;
      const protocolPrefix = brandingContext === "agenzia" ? "GA" : "GS";
      
      const saveData = {
        formType: "presa-in-carico",
        formName: `Mandato Pratica - ${formData.clientName}`,
        clientId: formData.clientId || null,
        clientName: formData.clientName,
        caseId: formData.linkedCaseId || null,
        caseNumber: linkedCase?.caseNumber || null,
        operatorId: formData.operatorId || null,
        operatorName: formData.operator,
        brandingContext: brandingContext,
        protocolPrefix: protocolPrefix,
        excludeOperatorFromPrint: true,
        formData: {
          clientName: formData.clientName,
          taxCode: formData.taxCode,
          vatNumber: formData.vatNumber,
          clientAddress: formData.clientAddress,
          clientEmail: formData.clientEmail,
          clientPhone: formData.clientPhone,
          clientSDI: formData.clientSDI,
          repName: formData.repName,
          repTaxCode: formData.repTaxCode,
          repPhone: formData.repPhone,
          repEmail: formData.repEmail,
          practiceType: formData.practiceType,
          practiceDescription: formData.practiceDescription,
          intakeDate: formData.intakeDate,
          operator: formData.operator,
          notes: formData.notes,
          linkedCaseId: formData.linkedCaseId || null
        }
      };
      
      let savedForm;
      if (editingForm) {
        savedForm = await api.updateForm(editingForm.id, saveData);
      } else {
        savedForm = await api.createForm(saveData);
      }
      
      setSaveSuccess(true);
      
      if (andPrint && savedForm) {
        try {
          await api.downloadFormPDF(savedForm.id);
        } catch (pdfError) {
          console.error("PDF error:", pdfError);
          alert("Modulo salvato, ma errore nella generazione PDF: " + pdfError.message);
        }
      }
      
      setTimeout(() => {
        if (onSaved) onSaved();
      }, 1000);
      
    } catch (error) {
      console.error("Save error:", error);
      alert("Errore durante il salvataggio: " + (error.message || "Errore sconosciuto"));
    } finally {
      setIsSaving(false);
    }
  }, [formData, brandingContext, cases, editingForm, validateForm, onSaved]);

  // Memoized operators list
  const activeOperators = useMemo(() => {
    return operators?.filter(op => op.status !== "sospeso") || [];
  }, [operators]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} data-testid="back-to-forms-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Moduli
        </Button>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {editingForm ? "Modifica Mandato" : "Mandato Pratica"}
          </h2>
          <p className="text-sm text-slate-500">
            Compila, salva e stampa il mandato
          </p>
        </div>
      </div>

      {/* Form Card */}
      <Card className="shadow-md">
        <CardHeader className="border-b py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-base">Mandato Pratica</CardTitle>
              <CardDescription className="text-xs">Gestionale Gruppo Impresa</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-5">
          <div className="space-y-5">
            
            {/* === DATI CLIENTE === */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <User className="h-4 w-4" />
                Dati Cliente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Client Search - STABLE component */}
                <div className="md:col-span-2">
                  <Label>Nome / Ragione Sociale *</Label>
                  <ClientSearchSelect
                    value={formData.clientName}
                    clients={clients || []}
                    onSelect={handleClientSelect}
                    onCreateNew={handleCreateNewClient}
                    error={errors.clientName}
                  />
                  <FieldError error={errors.clientName} />
                </div>
                
                <div>
                  <Label>Codice Fiscale *</Label>
                  <Input
                    value={formData.taxCode}
                    onChange={(e) => updateField("taxCode", e.target.value.toUpperCase())}
                    placeholder="Es. RSSMRA80A01H501X"
                    className={cn("text-sm", errors.taxCode && "border-red-500")}
                  />
                  <FieldError error={errors.taxCode} />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input
                    value={formData.vatNumber}
                    onChange={(e) => updateField("vatNumber", e.target.value)}
                    placeholder="Es. 01234567890"
                    className="text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Residenza / Sede Legale</Label>
                  <Input
                    value={formData.clientAddress}
                    onChange={(e) => updateField("clientAddress", e.target.value)}
                    placeholder="Indirizzo completo"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => updateField("clientEmail", e.target.value)}
                    placeholder="email@esempio.it"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label>Cellulare</Label>
                  <Input
                    value={formData.clientPhone}
                    onChange={(e) => updateField("clientPhone", e.target.value)}
                    placeholder="+39 333 1234567"
                    className="text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Codice SDI / PEC</Label>
                  <Input
                    value={formData.clientSDI}
                    onChange={(e) => updateField("clientSDI", e.target.value)}
                    placeholder="Codice SDI o indirizzo PEC"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* === PERSONA FISICA IN QUALITÀ DI (OPTIONAL) === */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Persona fisica in qualità di
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRepresentative(!showRepresentative)}
                  className="text-xs"
                >
                  {showRepresentative ? "Nascondi" : "Espandi"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                Compilare solo se il cliente agisce tramite un rappresentante (sezione opzionale)
              </p>
              
              {showRepresentative && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label>Cognome e Nome</Label>
                    <Input
                      value={formData.repName}
                      onChange={(e) => updateField("repName", e.target.value)}
                      placeholder="Es. Rossi Mario"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={formData.repTaxCode}
                      onChange={(e) => updateField("repTaxCode", e.target.value.toUpperCase())}
                      placeholder="Es. RSSMRA80A01H501X"
                      className={cn("text-sm", errors.repTaxCode && "border-red-500")}
                    />
                    <FieldError error={errors.repTaxCode} />
                  </div>
                  <div>
                    <Label>Telefono</Label>
                    <Input
                      value={formData.repPhone}
                      onChange={(e) => updateField("repPhone", e.target.value)}
                      placeholder="+39 333 1234567"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.repEmail}
                      onChange={(e) => updateField("repEmail", e.target.value)}
                      placeholder="email@esempio.it"
                      className="text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* === DATI PRATICA === */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Dati Pratica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Ente Coinvolto *</Label>
                  <Select 
                    value={formData.practiceType} 
                    onValueChange={(v) => updateField("practiceType", v)}
                  >
                    <SelectTrigger className={cn("text-sm", errors.practiceType && "border-red-500")}>
                      <SelectValue placeholder="Seleziona ente coinvolto" />
                    </SelectTrigger>
                    <SelectContent>
                      {enteCoinvoltoOptions.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError error={errors.practiceType} />
                </div>
                <div>
                  <Label>Data Mandato *</Label>
                  <Input
                    type="date"
                    value={formData.intakeDate}
                    onChange={(e) => updateField("intakeDate", e.target.value)}
                    className={cn("text-sm", errors.intakeDate && "border-red-500")}
                  />
                  <FieldError error={errors.intakeDate} />
                </div>
                <div className="md:col-span-2">
                  <Label>Descrizione Pratica *</Label>
                  <Textarea
                    value={formData.practiceDescription}
                    onChange={(e) => updateField("practiceDescription", e.target.value)}
                    placeholder="Descrizione della pratica..."
                    rows={2}
                    className={cn("text-sm", errors.practiceDescription && "border-red-500")}
                  />
                  <FieldError error={errors.practiceDescription} />
                </div>
              </div>
            </div>

            <Separator />

            {/* === OPERATORE === */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Operatore
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nominativo *</Label>
                  <Select
                    value={formData.operatorId || "none"}
                    onValueChange={(v) => {
                      if (v === "none") {
                        updateField("operator", "");
                        updateField("operatorId", "");
                      } else {
                        const op = activeOperators.find(o => o.id === v);
                        if (op) {
                          setFormData(prev => ({
                            ...prev,
                            operator: op.fullName,
                            operatorId: op.id
                          }));
                        }
                      }
                    }}
                  >
                    <SelectTrigger className={cn("text-sm", errors.operator && "border-red-500")}>
                      <SelectValue placeholder="Seleziona operatore" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Seleziona —</SelectItem>
                      {activeOperators.map(op => (
                        <SelectItem key={op.id} value={op.id}>{op.fullName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-600 mt-1">
                    Il nominativo NON verrà stampato nel documento
                  </p>
                  <FieldError error={errors.operator} />
                </div>
                <div>
                  <Label>Note</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    placeholder="Note aggiuntive..."
                    className="text-sm"
                  />
                </div>
                
                {/* Link to case */}
                {cases && cases.length > 0 && (
                  <div className="md:col-span-2">
                    <Label>Collega a Pratica (opzionale)</Label>
                    <Select 
                      value={formData.linkedCaseId || "none"} 
                      onValueChange={handleCaseSelect}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Nessuna pratica collegata" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuna pratica collegata —</SelectItem>
                        {cases.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.caseNumber} - {c.subject?.substring(0, 35) || 'N/D'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Branding selector */}
                <div className="md:col-span-2">
                  <Label>Intestazione (Protocollo)</Label>
                  {brandingLocked && (
                    <p className="text-xs text-amber-600 mb-1">
                      Determinato dalla pratica collegata
                    </p>
                  )}
                  <div className="flex gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => !brandingLocked && setBrandingContext("studio")}
                      disabled={brandingLocked}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg border-2 transition-all text-sm",
                        brandingLocked && "opacity-60 cursor-not-allowed",
                        brandingContext === "studio" ? "border-teal-500 bg-teal-50" : "border-slate-200"
                      )}
                    >
                      <Building2 className={cn("h-4 w-4", brandingContext === "studio" ? "text-teal-600" : "text-slate-400")} />
                      <span className={brandingContext === "studio" ? "text-teal-700 font-medium" : "text-slate-600"}>
                        Gestione Studio (GS-)
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => !brandingLocked && setBrandingContext("agenzia")}
                      disabled={brandingLocked}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg border-2 transition-all text-sm",
                        brandingLocked && "opacity-60 cursor-not-allowed",
                        brandingContext === "agenzia" ? "border-orange-500 bg-orange-50" : "border-slate-200"
                      )}
                    >
                      <Briefcase className={cn("h-4 w-4", brandingContext === "agenzia" ? "text-orange-600" : "text-slate-400")} />
                      <span className={brandingContext === "agenzia" ? "text-orange-700 font-medium" : "text-slate-600"}>
                        Gestione Agenzia (GA-)
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* === PRIVACY CONSENT (PAPER-STYLE) === */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Consenso Privacy (nel documento stampato)
              </h3>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600 mb-3">
                  Autorizzo il trattamento dei dati personali ai sensi del Reg. UE 2016/679 (GDPR)
                  per le finalità connesse alla gestione della pratica.
                </p>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-slate-400 rounded-sm bg-white"></div>
                    <span className="font-medium">SI</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-slate-400 rounded-sm bg-white"></div>
                    <span className="font-medium">NO</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2 italic">
                  Le caselle verranno stampate nel PDF per selezione manuale
                </p>
              </div>
            </div>

            <Separator />

            {/* === SIGNATURE AREA === */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Area Firme (nel PDF)</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-slate-600 mb-1">Firma del Cliente</p>
                  <div className="border-b-2 border-slate-300 h-8"></div>
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Firma per accettazione</p>
                  <div className="border-b-2 border-slate-300 h-8"></div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <div className="flex items-center gap-2 mr-auto">
          {saveSuccess && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Salvato!</span>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={onBack} disabled={isSaving}>
          Annulla
        </Button>
        <Button
          variant="outline"
          onClick={() => handleSave(false)}
          disabled={isSaving}
          data-testid="mandato-save-button"
        >
          {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salva
        </Button>
        <Button
          onClick={() => handleSave(true)}
          disabled={isSaving}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="mandato-save-print-button"
        >
          {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
          Salva e Stampa
        </Button>
      </div>
    </div>
  );
}
